import type { LayoutFn, Line, Segment } from "./base";
import { bar } from "./text";
import { blank, box, hstack, lines, separator, vstack } from "./layout";
import { type Column, table } from "./table";
import { statusbar, tabs } from "./widgets";

export type JsonNode =
  | { type: "vstack"; children: JsonNode[] }
  | { type: "hstack"; gap?: number; widths?: number[]; children: JsonNode[] }
  | { type: "box"; title?: string; borderColor?: string; children: JsonNode[] }
  | { type: "lines"; content: Line[] }
  | { type: "separator"; color?: string }
  | { type: "blank" }
  | {
      type: "table";
      columns: Column[];
      data: Record<string, unknown>[];
      borderColor?: string;
      fillWidth?: boolean;
    }
  | {
      type: "statusbar";
      left: Segment[];
      right: Segment[];
      bg?: string;
      color?: string;
    }
  | {
      type: "tabs";
      names: string[];
      active: number;
      activeColor?: string;
      activeBg?: string;
      inactiveColor?: string;
      separatorColor?: string;
    }
  | { type: "bar"; value: number; max: number; color?: string; label?: string }
  | { type: "text"; segments: Segment[] };

export function fromJsonNode(node: JsonNode): LayoutFn {
  switch (node.type) {
    case "vstack":
      return vstack(...node.children.map(fromJsonNode));

    case "hstack":
      return hstack(node.children.map(fromJsonNode), {
        gap: node.gap,
        widths: node.widths,
      });

    case "box":
      return box(vstack(...node.children.map(fromJsonNode)), {
        title: node.title,
        borderColor: node.borderColor,
      });

    case "lines":
      return lines(node.content);

    case "separator":
      return separator(node.color);

    case "blank":
      return blank();

    case "table":
      return table(node.columns, node.data, {
        borderColor: node.borderColor,
        fillWidth: node.fillWidth,
      });

    case "statusbar":
      return statusbar(node.left, node.right, {
        bg: node.bg,
        color: node.color,
      });

    case "tabs":
      return tabs(node.names, node.active, {
        activeColor: node.activeColor,
        activeBg: node.activeBg,
        inactiveColor: node.inactiveColor,
        separatorColor: node.separatorColor,
      });

    case "bar":
      return (w) => {
        const labelStr = node.label ? " " + node.label : "";
        const barW = Math.max(0, w - 1 - labelStr.length); // 1 for leading space
        return [[" ", { text: bar(node.value, node.max, barW), color: node.color }, labelStr]];
      };

    case "text":
      return lines([node.segments]);
  }
}
